import React from 'react';
import { Layout } from '../components/layout/Layout';
import { motion } from 'framer-motion';
import { Shield, Lock } from 'lucide-react';
export function PrivacyCookiePolicyPage() {
  return <Layout data-id="element-408">
      <div className="bg-gradient-to-br from-slate-50 to-teal-50 py-16" data-id="element-409">
        <div className="container mx-auto px-4 text-center" data-id="element-410">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} data-id="element-411">
            <h1 className="text-4xl font-bold text-slate-900 mb-4" data-id="element-412">
              Privacy & Cookie Policy
            </h1>
            <p className="text-slate-600 max-w-2xl mx-auto" data-id="element-413">
              Last updated:{' '}
              {new Date().toLocaleDateString('en-GB', {
              day: 'numeric',
              month: 'long',
              year: 'numeric'
            })}
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16" data-id="element-414">
        <div className="max-w-3xl mx-auto" data-id="element-415">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12" data-id="element-416">
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-start gap-4" data-id="element-417">
              <div className="p-3 bg-teal-50 text-teal-600 rounded-lg flex-shrink-0" data-id="element-418">
                <Shield size={24} data-id="element-419" />
              </div>
              <div data-id="element-420">
                <h3 className="font-semibold text-slate-900 mb-2" data-id="element-421">
                  GDPR Compliant
                </h3>
                <p className="text-slate-600 text-sm" data-id="element-422">
                  We follow UK data protection standards.
                </p>
              </div>
            </motion.div>

            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.1
          }} className="bg-white p-6 rounded-xl border border-slate-200 shadow-sm flex items-start gap-4" data-id="element-423">
              <div className="p-3 bg-teal-50 text-teal-600 rounded-lg flex-shrink-0" data-id="element-424">
                <Lock size={24} data-id="element-425" />
              </div>
              <div data-id="element-426">
                <h3 className="font-semibold text-slate-900 mb-2" data-id="element-427">
                  Secure Storage
                </h3>
                <p className="text-slate-600 text-sm" data-id="element-428">
                  Data stored in UK-based data centres.
                </p>
              </div>
            </motion.div>
          </div>

          <div className="prose prose-slate max-w-none" data-id="element-429">
            <p className="text-lg text-slate-600 leading-relaxed mb-8" data-id="element-430">
              At HomeLinkPro, we take your privacy seriously. This policy
              explains what personal data we collect, how we use it, and your
              rights under UK data protection law.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-431">
              Personal Data We Collect
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-432">
              When you use HomeLinkPro, we collect the following types of
              personal data:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6" data-id="element-433">
              <li data-id="element-434">
                <strong data-id="element-435">Name:</strong> To identify your account and personalise
                your experience
              </li>
              <li data-id="element-436">
                <strong data-id="element-437">Email address:</strong> For account access,
                communication, and service updates
              </li>
              <li data-id="element-438">
                <strong data-id="element-439">Usage activity:</strong> Information about how you use
                the platform, including features accessed, files uploaded, and
                tasks created
              </li>
              <li data-id="element-440">
                <strong data-id="element-441">Payment information:</strong> Processed securely by
                Stripe (we do not store your full card details)
              </li>
              <li data-id="element-442">
                <strong data-id="element-443">Device and browser information:</strong> To ensure
                compatibility and improve performance
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-444">
              How We Use Your Data
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-445">
              We use your personal data for the following purposes:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6" data-id="element-446">
              <li data-id="element-447">
                <strong data-id="element-448">Account management:</strong> To create and maintain your
                HomeLinkPro account
              </li>
              <li data-id="element-449">
                <strong data-id="element-450">Platform access:</strong> To provide you with access to
                our tools and features
              </li>
              <li data-id="element-451">
                <strong data-id="element-452">Service updates:</strong> To send you important
                information about your subscription, new features, and service
                changes
              </li>
              <li data-id="element-453">
                <strong data-id="element-454">Customer support:</strong> To respond to your enquiries
                and resolve issues
              </li>
              <li data-id="element-455">
                <strong data-id="element-456">Performance improvement:</strong> To analyse usage
                patterns and improve our platform
              </li>
              <li data-id="element-457">
                <strong data-id="element-458">Legal compliance:</strong> To meet our legal obligations
                and protect our rights
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-459">
              Cookie Usage
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-460">
              We use cookies to enhance your experience on our website and
              platform. Cookies are small text files stored on your device that
              help us:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6" data-id="element-461">
              <li data-id="element-462">
                <strong data-id="element-463">Essential cookies:</strong> Required for the platform to
                function (e.g., keeping you logged in)
              </li>
              <li data-id="element-464">
                <strong data-id="element-465">Performance cookies:</strong> Help us understand how you
                use the platform to improve it
              </li>
              <li data-id="element-466">
                <strong data-id="element-467">Functional cookies:</strong> Remember your preferences
                and settings
              </li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-468">
              You can control cookies through your browser settings, but
              disabling essential cookies may affect your ability to use certain
              features.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-469">
              Your GDPR Rights
            </h2>
            <p className="text-slate-600 leading-relaxed mb-4" data-id="element-470">
              Under UK data protection law (GDPR), you have the following
              rights:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6" data-id="element-471">
              <li data-id="element-472">
                <strong data-id="element-473">Right to access:</strong> Request a copy of the personal
                data we hold about you
              </li>
              <li data-id="element-474">
                <strong data-id="element-475">Right to rectification:</strong> Ask us to correct
                inaccurate or incomplete data
              </li>
              <li data-id="element-476">
                <strong data-id="element-477">Right to erasure:</strong> Request deletion of your
                personal data (subject to legal obligations)
              </li>
              <li data-id="element-478">
                <strong data-id="element-479">Right to restrict processing:</strong> Ask us to limit
                how we use your data
              </li>
              <li data-id="element-480">
                <strong data-id="element-481">Right to data portability:</strong> Receive your data in
                a structured, machine-readable format
              </li>
              <li data-id="element-482">
                <strong data-id="element-483">Right to object:</strong> Object to certain types of
                data processing
              </li>
              <li data-id="element-484">
                <strong data-id="element-485">Right to withdraw consent:</strong> Withdraw your
                consent for data processing at any time
              </li>
            </ul>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-486">
              To exercise any of these rights, please contact us at{' '}
              <a href="mailto:lbhuwan151@gmail.com" className="text-teal-600 hover:text-teal-700" data-id="element-487">
                lbhuwan151@gmail.com
              </a>
              .
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-488">
              Data Retention
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-489">
              We retain your personal data for as long as your account is active
              or as needed to provide you with our services. If you cancel your
              subscription, we will retain your data for 30 days to allow you to
              reactivate your account. After 30 days, your data will be
              permanently deleted, except where we are required to retain it for
              legal or regulatory purposes.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-490">
              Data Security
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-491">
              We take data security seriously and implement appropriate
              technical and organisational measures to protect your personal
              data, including:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6" data-id="element-492">
              <li data-id="element-493">Bank-grade encryption for data in transit and at rest</li>
              <li data-id="element-494">Secure UK-based data centres with 24/7 monitoring</li>
              <li data-id="element-495">
                Role-based access controls to limit who can access your data
              </li>
              <li data-id="element-496">Regular security audits and updates</li>
              <li data-id="element-497">
                Staff training on data protection and security best practices
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-498">
              Data Sharing
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-499">
              We do not sell, rent, or share your personal data with third
              parties for marketing purposes. We may share your data with:
            </p>
            <ul className="list-disc pl-6 text-slate-600 space-y-2 mb-6" data-id="element-500">
              <li data-id="element-501">
                <strong data-id="element-502">Service providers:</strong> Such as Stripe for payment
                processing (they have their own privacy policies)
              </li>
              <li data-id="element-503">
                <strong data-id="element-504">Legal authorities:</strong> If required by law or to
                protect our rights
              </li>
            </ul>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-505">
              Changes to This Policy
            </h2>
            <p className="text-slate-600 leading-relaxed mb-6" data-id="element-506">
              We may update this Privacy & Cookie Policy from time to time. We
              will notify you of any significant changes by email or through a
              notice on our platform. Your continued use of HomeLinkPro after
              changes are made constitutes your acceptance of the updated
              policy.
            </p>

            <h2 className="text-2xl font-bold text-slate-900 mb-4" data-id="element-507">
              Contact Us
            </h2>
            <p className="text-slate-600 leading-relaxed" data-id="element-508">
              If you have any questions about this Privacy & Cookie Policy or
              how we handle your data, please contact us at{' '}
              <a href="mailto:lbhuwan151@gmail.com" className="text-teal-600 hover:text-teal-700" data-id="element-509">
                lbhuwan151@gmail.com
              </a>{' '}
              or visit our{' '}
              <a href="/contact" className="text-teal-600 hover:text-teal-700" data-id="element-510">
                Contact Us
              </a>{' '}
              page.
            </p>
          </div>
        </div>
      </div>
    </Layout>;
}